import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, Shield, Database, RefreshCw } from "lucide-react";
import { API, BACKEND_URL, COVER_IMAGE, SSB_PLACEHOLDER } from "../lib/constants";
import { useAdmin } from "../contexts/AdminContext";
import ProductionStatusWidget from "../components/ProductionStatusWidget";
import ContinueListening from "../components/ContinueListening";

const HERO_BG = `${BACKEND_URL}/api/files/hero-bg`;

export default function Home() {
  const [allSeries, setAllSeries] = useState([]);
  const [lastRead, setLastRead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dbEmpty, setDbEmpty] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/series`);
      setAllSeries(res.data);
      setDbEmpty(res.data.length === 0);
    } catch (e) {
      console.error(e);
      setDbEmpty(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    try {
      const saved = localStorage.getItem('lastReadEpisode');
      if (saved) setLastRead(JSON.parse(saved));
    } catch (e) {}
  }, []);

  const handleManualSeed = async () => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const adminToken = localStorage.getItem("admin_token") || "";
      const res = await axios.post(`${API}/seed`, {}, {
        headers: adminToken ? { "X-Admin-Token": adminToken } : {},
        withCredentials: true,
      });
      setSeedResult(res.data);
      if (res.data.seeded) {
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setSeedResult({ message: `Error: ${msg}`, seeded: false });
    } finally {
      setSeeding(false);
    }
  };

  const handleForceSeed = async () => {
    if (!window.confirm("⚠️ Force Re-seed will DROP all content collections and re-insert from seed_data.json. This is destructive. Continue?")) {
      return;
    }
    setSeeding(true);
    setSeedResult(null);
    try {
      const adminToken = localStorage.getItem("admin_token") || "";
      const res = await axios.post(`${API}/seed/force`, {}, {
        headers: adminToken ? { "X-Admin-Token": adminToken } : {},
        withCredentials: true,
      });
      setSeedResult(res.data);
      if (res.data.seeded) {
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setSeedResult({ message: `Error: ${msg}`, seeded: false });
    } finally {
      setSeeding(false);
    }
  };

  const handleSeriesClick = (series) => {
    if (series.content_rating === '17+') {
      const verified = localStorage.getItem('age_verified_ssb');
      if (!verified) {
        navigate(`/series/${series.slug}`, { state: { needsAgeGate: true } });
        return;
      }
    }
    navigate(`/series/${series.slug}`);
  };

  const { isAdmin } = useAdmin();

  return (
    <div data-testid="home-page">
      <section className="hero-section grain pt-24 md:pt-28">
        <div className="hero-bg" style={{ backgroundImage: `url(${HERO_BG}), url(${COVER_IMAGE})` }} />
        <div className="hero-overlay" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <p className="font-mono text-red-500 text-sm tracking-[0.3em] mb-2 fade-in">RED INK STUDIOS PRESENTS</p>
          <h1 className="font-cinzel text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-6 neon-red fade-in" style={{ animationDelay: '0.2s' }}>
            RED INK<br />STUDIOS
          </h1>
          <p className="font-manrope text-gray-400 text-lg md:text-xl max-w-2xl mx-auto mb-10 fade-in" style={{ animationDelay: '0.4s' }}>
            Premium cinematic audio dramas, books, and music. Where every signature has a story.
          </p>
        </div>
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronRight size={24} className="rotate-90 text-gray-600" />
        </div>
      </section>

      {dbEmpty && !loading && (
        <section className="px-6 py-8 bg-red-950/30 border-y border-red-500/20" data-testid="db-init-banner">
          <div className="max-w-2xl mx-auto text-center">
            <Database size={40} className="mx-auto mb-3 text-red-500" />
            <h3 className="font-cinzel text-xl font-bold text-white mb-2">{isAdmin ? "Database Not Initialized" : "Content Loading"}</h3>
            <p className="text-gray-400 text-sm mb-4">
              {isAdmin
                ? "The content library hasn't been loaded yet. Click below to initialize the database with all episodes, characters, and music."
                : "Our story library is being prepared. Please check back in a few minutes — premium cinematic audio dramas are on their way."}
            </p>
            {isAdmin && (
              <>
                <div className="flex gap-3 justify-center flex-wrap">
                  <button
                    onClick={handleManualSeed}
                    disabled={seeding}
                    className="px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:opacity-50 text-white font-bold rounded-lg transition-colors flex items-center gap-2"
                    data-testid="init-db-button"
                  >
                    {seeding ? <RefreshCw size={16} className="animate-spin" /> : <Database size={16} />}
                    {seeding ? "Initializing..." : "Initialize Database"}
                  </button>
                  <button
                    onClick={handleForceSeed}
                    disabled={seeding}
                    className="px-6 py-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-bold rounded-lg transition-colors flex items-center gap-2"
                    data-testid="force-seed-button"
                  >
                    {seeding ? <RefreshCw size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                    Force Re-seed
                  </button>
                </div>
                {seedResult && (
                  <div className={`mt-4 p-3 rounded text-sm font-mono ${seedResult.seeded ? 'bg-green-900/50 text-green-300 border border-green-500/30' : 'bg-red-900/50 text-red-300 border border-red-500/30'}`} data-testid="seed-result">
                    <p className="font-bold mb-1">{seedResult.message}</p>
                    {seedResult.details && (
                      <ul className="text-left text-xs space-y-0.5 mt-2">
                        {Object.entries(seedResult.details).map(([k, v]) => (
                          <li key={k}>{k}: {v}</li>
                        ))}
                      </ul>
                    )}
                {seedResult.seeded && <p className="mt-2 text-green-400 font-bold">Reloading page...</p>}
              </div>
            )}
              </>
            )}
          </div>
        </section>
      )}

      {lastRead && (
        <section className="px-6 -mt-6 relative z-20" data-testid="continue-reading-section">
          <div className="max-w-4xl mx-auto">
            <button onClick={() => navigate(`/episodes/${lastRead.id}`)} className="w-full flex items-center gap-4 p-4 bg-red-950/60 border border-red-500/30 rounded-lg backdrop-blur-sm hover:bg-red-950/80 transition-all group" data-testid="continue-reading-btn">
              <div className="w-14 h-14 bg-cover bg-center rounded flex-shrink-0" style={{ backgroundImage: `url(${lastRead.thumbnail_url ? `${BACKEND_URL}${lastRead.thumbnail_url}` : COVER_IMAGE})` }} />
              <div className="flex-1 text-left">
                <p className="text-xs text-red-400 font-mono tracking-wider mb-0.5">CONTINUE WHERE YOU LEFT OFF</p>
                <p className="font-cinzel font-bold text-white group-hover:text-red-400 transition-colors">
                  S{lastRead.season}E{lastRead.episode_number}: {lastRead.title}
                </p>
              </div>
              <ChevronRight size={24} className="text-red-500 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </section>
      )}

      <ContinueListening />

      {isAdmin && (
        <section className="px-6 mt-6 relative z-20" data-testid="admin-production-status-section">
          <div className="max-w-4xl mx-auto">
            <ProductionStatusWidget />
          </div>
        </section>
      )}

      <section className="py-20 px-6 bg-gradient-to-b from-[#050505] to-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-cinzel text-3xl md:text-4xl text-center mb-4 text-red-500">OUR SERIES</h2>
          <p className="text-center text-gray-500 mb-12 text-sm tracking-wider">CHOOSE YOUR STORY</p>
          {loading ? (
            <div className="text-center py-12"><div className="w-12 h-12 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 stagger-children">
              {allSeries.map((series) => (
                <button key={series.slug} onClick={() => handleSeriesClick(series)} className="group relative overflow-hidden rounded-lg border border-white/10 hover:border-red-500/40 transition-all text-left bg-[#0a0a0a]" data-testid={`series-card-${series.slug}`}>
                  <div className="h-64 relative overflow-hidden">
                    <div className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700" style={{ backgroundImage: `url(${series.cover_image || SSB_PLACEHOLDER})` }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
                    {series.content_rating === '17+' && (
                      <div className="absolute top-4 right-4 px-3 py-1 bg-red-600 text-white text-xs font-bold tracking-wider rounded flex items-center gap-1.5"><Shield size={12} />17+</div>
                    )}
                    {series.content_rating === 'PG-13' && (
                      <div className="absolute top-4 right-4 px-2 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-bold tracking-wider rounded">PG-13</div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-cinzel text-2xl font-bold mb-2 group-hover:text-red-500 transition-colors">{series.title || series.name}</h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-3">{series.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="font-mono">{series.seasons} SEASONS</span>
                        <span className="font-mono">{series.total_episodes} EPISODES</span>
                      </div>
                      <ChevronRight size={20} className="text-red-500 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
